import { Component, h, formatWhen, register } from './core.js';
import { renderIcon } from './icons.js';

/**
 * Chat: a conversation view with a composer. The host owns delivery: it
 * listens for `send`, calls its API, then `add()`s the message (or marks
 * a pending one as sent/failed).
 *
 * new dfc.Chat(container, {
 *   user: { id, name, avatar },              // who is "me": their messages align right
 *   messages: [{ id, text, author: { id, name, avatar }, at, status: 'sent'|'pending'|'failed',
 *                attachments: [{ id, label, icon, meta }], meta }],
 *   title: null, showHeader: false, placeholder: 'Write a message…', sendOnEnter: true,
 *   groupByDay: true, avatars: true, disabled: false, maxLength: 4000,
 *   renderMessage: null | (message, isMine) => Element | string, html: false,
 *   emptyText: 'No messages yet.'
 * })
 * Events: send { text, reset() }, messageClick { message }, attachmentClick { message, attachment },
 *         input { text }, valueChanged { value (draft), previous, source }
 * Methods: add(message), update(id, patch), remove(id), setMessages(list), scrollToBottom(),
 *          setTyping(name | null), focus(), clear()
 */
export class Chat extends Component {
	static get componentName() { return 'chat'; }

	defaults() {
		return { user: null, messages: [], value: '', title: null, showHeader: false, placeholder: 'Write a message…', sendOnEnter: true, groupByDay: true, avatars: true, disabled: false, maxLength: 4000, renderMessage: null, html: false, emptyText: 'No messages yet.', sendLabel: 'Send' };
	}

	render() {
		const o = this.options;
		const firstRender = !this.listEl;
		const draft = this.inputEl ? this.inputEl.value : (o.value || '');
		this._messages = Chat._sorted(o.messages);
		this.el.className = 'dfc dfc-chat' + (o.disabled ? ' is-disabled' : '') + (o.className ? ' ' + o.className : '');
		this.el.innerHTML = '';
		if (o.showHeader) {
			this.el.appendChild(h('div', { className: 'dfc-chat-header' }, h('span', { className: 'dfc-chat-title', text: o.title || '' })));
		}
		this.listEl = h('div', { className: 'dfc-chat-messages', role: 'log', 'aria-live': 'polite', 'aria-relevant': 'additions' });
		this._renderMessages();
		this.el.appendChild(this.listEl);
		this.typingEl = h('div', { className: 'dfc-chat-typing', hidden: true });
		this.el.appendChild(this.typingEl);
		this.inputEl = h('textarea', { className: 'dfc-chat-input', rows: '1', placeholder: o.placeholder, maxlength: String(o.maxLength), disabled: o.disabled ? true : null, 'aria-label': o.placeholder });
		this.inputEl.value = draft;
		this.sendBtn = h('button', { type: 'button', className: 'dfc-chat-send', title: o.sendLabel, 'aria-label': o.sendLabel, disabled: o.disabled ? true : null }, renderIcon('send'));
		this.el.appendChild(h('div', { className: 'dfc-chat-composer' }, this.inputEl, this.sendBtn));
		this._autosize();
		if (firstRender) requestAnimationFrame(() => this.scrollToBottom(false)); else this.scrollToBottom(false);
	}

	_renderMessages() {
		const o = this.options;
		this.listEl.innerHTML = '';
		if (!this._messages.length) {
			this.listEl.appendChild(h('div', { className: 'dfc-chat-empty', text: o.emptyText }));
			return;
		}
		let lastDay = null, lastAuthor = null;
		this._messages.forEach((m) => {
			const when = formatWhen(m.at, o.locale);
			if (o.groupByDay && when.dayKey !== lastDay) {
				this.listEl.appendChild(h('div', { className: 'dfc-chat-day', role: 'separator' }, h('span', { text: when.date })));
				lastDay = when.dayKey; lastAuthor = null;
			}
			const mine = !!(o.user && m.author && String(m.author.id) === String(o.user.id));
			const authorId = m.author ? String(m.author.id) : '';
			const continued = lastAuthor === authorId;
			lastAuthor = authorId;
			this.listEl.appendChild(this._renderMessage(m, mine, continued, when));
		});
	}

	_renderMessage(m, mine, continued, when) {
		const o = this.options;
		let body;
		if (typeof o.renderMessage === 'function') {
			const out = o.renderMessage(m, mine);
			body = out instanceof Node ? out : h('div', { className: 'dfc-chat-text', [o.html ? 'html' : 'text']: out || '' });
		} else {
			body = h('div', { className: 'dfc-chat-text', [o.html ? 'html' : 'text']: m.text || '' });
		}
		const attachments = (m.attachments || []).map((a) => h('button', {
			type: 'button', className: 'dfc-chat-attachment', dataset: { attachment: String(a.id) }, title: a.label,
		}, renderIcon(a.icon || 'file'), h('span', { text: a.label || 'Attachment' })));
		const avatar = o.avatars && !mine ? h('span', { className: 'dfc-chat-avatar', 'aria-hidden': 'true' },
			m.author && m.author.avatar ? h('img', { src: m.author.avatar, alt: '' }) : h('span', { text: Chat._initials(m.author && m.author.name) })) : null;
		return h('div', { className: `dfc-chat-message ${mine ? 'is-mine' : 'is-theirs'}${continued ? ' is-continued' : ''}${m.status ? ' is-' + m.status : ''}`, dataset: { id: String(m.id) } },
			avatar,
			h('div', { className: 'dfc-chat-bubble' },
				!mine && !continued && m.author && m.author.name ? h('div', { className: 'dfc-chat-author', text: m.author.name }) : null,
				body,
				attachments.length ? h('div', { className: 'dfc-chat-attachments' }, attachments) : null,
				h('div', { className: 'dfc-chat-meta' },
					h('span', { className: 'dfc-chat-time', text: when.time, title: when.date + ' ' + when.time }),
					m.status === 'pending' ? renderIcon('clock', 'dfc-chat-status') : m.status === 'failed' ? renderIcon('warning', 'dfc-chat-status') : null)));
	}

	bind() {
		this._onClick = (e) => {
			const att = e.target.closest && e.target.closest('.dfc-chat-attachment');
			const msgEl = e.target.closest && e.target.closest('.dfc-chat-message');
			if (!msgEl || !this.el.contains(msgEl)) return;
			const message = this._messages.find((x) => String(x.id) === msgEl.dataset.id);
			if (!message) return;
			if (att) {
				const attachment = (message.attachments || []).find((a) => String(a.id) === att.dataset.attachment);
				this.emit('attachmentClick', { message, attachment });
				return;
			}
			this.emit('messageClick', { message });
		};
		this._onSend = () => this._send();
		this._onKey = (e) => {
			if (e.key === 'Enter' && !e.shiftKey && this.options.sendOnEnter) { e.preventDefault(); this._send(); }
		};
		this._onInput = () => { this._autosize(); const prev = this._lastDraft || ''; this._lastDraft = this.inputEl.value; this.emit('input', { text: this.inputEl.value }); this._valueChanged(this.inputEl.value, prev, 'user'); };
		this.el.addEventListener('click', this._onClick);
		this.el.addEventListener('keydown', this._onKeyDelegated = (e) => { if (e.target === this.inputEl) this._onKey(e); });
		this.el.addEventListener('input', this._onInputDelegated = (e) => { if (e.target === this.inputEl) this._onInput(); });
		this.el.addEventListener('click', this._onSendDelegated = (e) => { if (e.target.closest && e.target.closest('.dfc-chat-send')) this._onSend(); });
	}

	unbind() {
		if (!this.el) return;
		this.el.removeEventListener('click', this._onClick);
		this.el.removeEventListener('keydown', this._onKeyDelegated);
		this.el.removeEventListener('input', this._onInputDelegated);
		this.el.removeEventListener('click', this._onSendDelegated);
	}

	_send() {
		if (this.options.disabled) return;
		const text = this.inputEl.value.trim();
		if (!text) return;
		this.emit('send', { text, reset: () => this.clear() });
	}

	_autosize() {
		const ta = this.inputEl;
		if (!ta) return;
		ta.style.height = 'auto';
		ta.style.height = Math.min(ta.scrollHeight, 160) + 'px';
	}

	get messages() { return this._messages.slice(); }
	get text() { return this.inputEl ? this.inputEl.value : ''; }
	/** value = the composer draft. */
	get value() { return this.text; }
	set value(v) { const prev = this.text; if (this.inputEl) { this.inputEl.value = v === undefined || v === null ? '' : String(v); this._autosize(); } this._lastDraft = this.text; this._valueChanged(this.text, prev, 'api'); }

	setMessages(list) { return this.update({ messages: Array.isArray(list) ? list.slice() : [] }); }

	/** Adds or replaces a message (by id) and scrolls to it. */
	add(message) {
		const messages = this.options.messages.filter((x) => String(x.id) !== String(message.id));
		messages.push(message);
		this.options.messages = messages;
		this._messages = Chat._sorted(messages);
		this._renderMessages();
		this.scrollToBottom(true);
		return this;
	}

	/** Patches a message in place (e.g. status pending → sent). */
	updateMessage(id, patch) {
		const m = this.options.messages.find((x) => String(x.id) === String(id));
		if (m) { Object.assign(m, patch); this._messages = Chat._sorted(this.options.messages); this._renderMessages(); }
		return this;
	}

	remove(id) {
		this.options.messages = this.options.messages.filter((x) => String(x.id) !== String(id));
		this._messages = Chat._sorted(this.options.messages);
		this._renderMessages();
		return this;
	}

	setTyping(name) {
		if (!this.typingEl) return this;
		if (name) { this.typingEl.textContent = `${name} is typing…`; this.typingEl.hidden = false; } else { this.typingEl.hidden = true; }
		return this;
	}

	scrollToBottom(smooth) {
		if (this.listEl) this.listEl.scrollTo({ top: this.listEl.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
		return this;
	}

	focus() { if (this.inputEl) this.inputEl.focus(); return this; }
	clear() { this.value = ''; return this; }

	static _sorted(list) {
		const t = (x) => { const d = x.at instanceof Date ? x.at : new Date(x.at); return isNaN(d.getTime()) ? 0 : d.getTime(); };
		return (list || []).slice().sort((a, b) => t(a) - t(b));
	}
	static _initials(name) {
		return String(name || '?').trim().split(/\s+/).slice(0, 2).map((w) => w[0] ? w[0].toUpperCase() : '').join('') || '?';
	}
}

register('chat', Chat);
